import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Code, WhatsApp, Star, ArrowRight, Shield } from '@/components/ui/Icons';
import { Logo } from '@/components/ui/Logo';
import { Socials } from '@/components/ui/Socials';
import { openCookieConsent } from '@/components/ui/CookieConsent';
import { company } from '@/data/company';
import { services } from '@/data/services';

export function Footer() {
  return (
    <footer className="relative isolate overflow-hidden bg-navy-900 text-paper-0">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 -z-10 h-1 gold-divider"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_15%,rgba(184,145,70,0.10),transparent_55%)]"
      />

      <div className="container-page grid gap-12 py-16 md:grid-cols-12">
        {/* Brand block */}
        <div className="md:col-span-4">
          <Link to="/" className="inline-flex items-center gap-3" aria-label="Bliss Building Contractors home">
            <Logo decorative interactive className="h-20" />
          </Link>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-paper-0/75">
            Your local provider of comprehensive contracting services across {company.serviceCoverage}.
          </p>
          <div className="mt-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-gold-300">Follow us</p>
            <Socials variant="navy" />
          </div>
        </div>

        {/* Contact */}
        <div className="md:col-span-4">
          <h2 className="font-display text-xl font-semibold text-gold-300">Get in touch</h2>
          <ul className="mt-5 space-y-4 text-paper-0/90">
            <li>
              <a href={`tel:${company.phoneTel}`} className="flex items-start gap-3 hover:text-gold-300">
                <span className="mt-1 grid h-9 w-9 place-items-center rounded-full bg-navy-800 ring-1 ring-paper-0/10">
                  <Phone className="h-4 w-4 text-gold-400" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wider text-paper-0/60">Freephone</span>
                  <span className="text-lg font-semibold">{company.phone}</span>
                </span>
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${company.whatsappTel}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 hover:text-gold-300"
              >
                <span className="mt-1 grid h-9 w-9 place-items-center rounded-full bg-[#25D366]/15 ring-1 ring-[#25D366]/40">
                  <WhatsApp className="h-4 w-4 text-[#25D366]" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wider text-paper-0/60">WhatsApp / mobile</span>
                  <span className="font-semibold">{company.mobile}</span>
                </span>
              </a>
            </li>
            <li>
              <a href={`mailto:${company.email}`} className="flex items-start gap-3 break-all hover:text-gold-300">
                <span className="mt-1 grid h-9 w-9 place-items-center rounded-full bg-navy-800 ring-1 ring-paper-0/10">
                  <Mail className="h-4 w-4 text-gold-400" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wider text-paper-0/60">Email</span>
                  <span className="font-medium">{company.email}</span>
                </span>
              </a>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 grid h-9 w-9 place-items-center rounded-full bg-navy-800 ring-1 ring-paper-0/10">
                <MapPin className="h-4 w-4 text-gold-400" />
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-wider text-paper-0/60">Coverage</span>
                <span className="font-medium">{company.serviceCoverage}</span>
              </span>
            </li>
          </ul>

          <a
            href={company.google.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold-400/60 bg-navy-800 px-4 py-2 text-sm font-semibold text-gold-300 transition hover:bg-gold-400 hover:text-navy-900"
          >
            <Star className="h-3.5 w-3.5" />
            Leave a Google review
            <ArrowRight className="h-3 w-3" />
          </a>
        </div>

        {/* Services */}
        <div className="md:col-span-4">
          <h2 className="font-display text-xl font-semibold text-gold-300">Our contracting services</h2>
          <ul className="chevron-list mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-paper-0/85">
            {services.map((s) => (
              <li key={s.slug}>
                <Link to={`/${s.slug}`} className="hover:text-gold-300">{s.navLabel}</Link>
              </li>
            ))}
            <li><Link to="/about" className="hover:text-gold-300">About us</Link></li>
            <li><Link to="/contact" className="hover:text-gold-300">Contact</Link></li>
          </ul>

          <h3 className="mt-7 text-xs font-semibold uppercase tracking-[0.22em] text-gold-300">Areas covered</h3>
          <p className="mt-3 text-sm leading-relaxed text-paper-0/75">
            {company.serviceTowns.join(' · ')}
          </p>
        </div>
      </div>

      <div className="border-t border-navy-700">
        <div className="container-page flex flex-col items-center justify-between gap-3 py-5 text-xs text-paper-0/60 sm:flex-row">
          <p>© {new Date().getFullYear()} {company.legalName}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <button
              type="button"
              onClick={() => openCookieConsent()}
              className="inline-flex items-center gap-1.5 text-paper-0/60 transition hover:text-gold-300"
            >
              <Shield className="h-3 w-3" />
              Cookie settings
            </button>
            <a
              href="https://codedvisiondesign.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-paper-0/60 transition hover:text-gold-300"
            >
              <Code className="h-3 w-3" /> by Coded Vision Design
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
